import { useEffect, useRef, useState } from 'react';
import AutoSyncSettings from './AutoSyncSettings';
import ManualSyncPanel from './ManualSyncPanel';
import styles from '../GirderConnection.module.css';

/**
 * Sync card: collapsible shell; Auto-sync primary; Manual sync secondary.
 * History sits outside <summary> so it does not toggle details.
 */
export default function SyncWorkspace({
    showAutoSync,
    connection,
    updateAutoPull,
    syncMode,
    onSyncModeChange,
    isSyncInProgress,
    connectionId,
    folderId,
    onFullSync,
    onCheckForChanges,
    onSyncSelected,
    onResetSync,
    syncRemotePending,
    fetchDiffPending,
    resetSyncPending,
    selectedCount,
    syncRecordStatus,
    isStuck,
    remoteName,
    syncRecord,
    diffReview,
    syncError,
    onDismissError,
    onDiscardDiff,
    selectedUuids,
    onSelectionChange,
    onOpenHistory,
}) {
    const autoImportOn = !!(connection?.autoPull?.enabled);

    const shouldForcePanelOpen =
        !showAutoSync
        || !autoImportOn
        || isSyncInProgress
        || !!diffReview
        || !!syncError;

    const shouldForceManualOpen = shouldForcePanelOpen;

    const [panelOpen, setPanelOpen] = useState(shouldForcePanelOpen);
    const panelUserToggledRef = useRef(false);

    const [manualOpen, setManualOpen] = useState(shouldForceManualOpen);
    const manualUserToggledRef = useRef(false);

    useEffect(() => {
        if (shouldForcePanelOpen) {
            setPanelOpen(true);
            return;
        }
        if (!panelUserToggledRef.current) {
            setPanelOpen(false);
        }
    }, [shouldForcePanelOpen]);

    useEffect(() => {
        if (shouldForceManualOpen) {
            setManualOpen(true);
            return;
        }
        if (!manualUserToggledRef.current) {
            setManualOpen(false);
        }
    }, [shouldForceManualOpen]);

    const handlePanelToggle = (e) => {
        if (e.target !== e.currentTarget) return;
        panelUserToggledRef.current = true;
        setPanelOpen(e.currentTarget.open);
    };

    const handleManualToggle = (e) => {
        if (e.target !== e.currentTarget) return;
        manualUserToggledRef.current = true;
        setManualOpen(e.currentTarget.open);
    };

    return (
        <div className={styles.syncWorkspaceShell}>
            <div className={styles.syncWorkspaceHeader}>
                <details
                    className={styles.syncWorkspace}
                    aria-label="Data sync"
                    open={panelOpen}
                    onToggle={handlePanelToggle}
                >
                    <summary className={styles.syncWorkspaceSummary}>
                        <span className={styles.disclosureSummaryMain}>
                            <span className={styles.disclosureChevron} aria-hidden="true" />
                            <span className={styles.syncWorkspaceTitle}>Sync</span>
                        </span>
                        <span className={styles.disclosureSummaryAction}>
                            {panelOpen ? 'Hide' : 'Show'}
                        </span>
                    </summary>

                    <div className={styles.syncWorkspaceBody}>
                        {showAutoSync && (
                            <AutoSyncSettings
                                connection={connection}
                                updateAutoPull={updateAutoPull}
                            />
                        )}

                        <details
                            className={styles.manualSyncDisclosure}
                            open={manualOpen}
                            onToggle={handleManualToggle}
                        >
                            <summary className={styles.manualSyncSummary}>
                                <span className={styles.disclosureSummaryMain}>
                                    <span className={styles.disclosureChevron} aria-hidden="true" />
                                    <span className={styles.manualSyncSummaryTitle}>Manual sync</span>
                                </span>
                                <span className={styles.disclosureSummaryAction}>
                                    {manualOpen ? 'Hide' : 'Show'}
                                </span>
                            </summary>
                            <p className={styles.manualSyncSummaryHint}>
                                Run a sync now (all changes or choose what to sync)
                            </p>
                            <ManualSyncPanel
                                syncMode={syncMode}
                                onSyncModeChange={onSyncModeChange}
                                isSyncInProgress={isSyncInProgress}
                                connectionId={connectionId}
                                folderId={folderId}
                                onFullSync={onFullSync}
                                onCheckForChanges={onCheckForChanges}
                                onSyncSelected={onSyncSelected}
                                onResetSync={onResetSync}
                                syncRemotePending={syncRemotePending}
                                fetchDiffPending={fetchDiffPending}
                                resetSyncPending={resetSyncPending}
                                selectedCount={selectedCount}
                                syncRecordStatus={syncRecordStatus}
                                isStuck={isStuck}
                                remoteName={remoteName}
                                syncRecord={syncRecord}
                                diffReview={diffReview}
                                syncError={syncError}
                                onDismissError={onDismissError}
                                onDiscardDiff={onDiscardDiff}
                                selectedUuids={selectedUuids}
                                onSelectionChange={onSelectionChange}
                            />
                        </details>
                    </div>
                </details>
                {onOpenHistory && (
                    <button
                        type="button"
                        className={styles.syncHistoryButton}
                        onClick={onOpenHistory}
                    >
                        History
                    </button>
                )}
            </div>
        </div>
    );
}
